/**
 * Pure ffmpeg-command operations for video tools -- the video-category
 * equivalent of pdf/operations.ts and image/operations.ts. Deliberately
 * narrower in scope than those: video processing is inherently
 * long-running with progress callbacks and an already-loaded ffmpeg
 * instance shared across potentially several operations (TrimVideo
 * writes the input file once, then loops this module's trim function
 * once per segment against that same instance) -- so the ffmpeg
 * lifecycle (loadFFmpeg/resetFFmpegEngine) and the input file write
 * stay in the component, not here. What's extracted is the actual
 * command-building and execution for one operation: given an
 * already-loaded ffmpeg instance and an already-written input path,
 * run this specific transform and return the raw output bytes.
 *
 * Re-exports the video utilities already in utils.ts (which itself
 * re-exports the shared ffmpeg engine from lib/ffmpegEngine) so this
 * becomes the single place to import video-tool operation logic from
 * going forward.
 */
import { needsBrowserShaping, renderCueStrips, buildStripList } from "./subtitleOverlay";

export {
  compressVideoToTarget,
  type VideoTargetSizeResult,
  loadFFmpeg,
  execWithProgress,
  fetchFile,
  ffmpegOutputToBlob,
  checkFileSize,
  stopProcessing,
  resetFFmpegEngine,
  getVideoDurationSeconds,
  formatBytes,
  getMaxFileSize,
  FREE_MAX_BYTES,
  PRO_MAX_BYTES,
  FirewallBlockedError,
  UserCancelledError,
  type FileSizeSeverity,
  type FileSizeCheck,
} from "./utils";

import type { FFmpeg } from "@ffmpeg/ffmpeg";
import { execWithProgress, fetchFile, probeMedia, type MediaProbe } from "./utils";
import { planMerge, mergeEncodeArgs, CROSSFADE_SECONDS, type MergeShape } from "./mergePlan";
import { parseSrt, buildAss, DEFAULT_SUBTITLE_STYLE, type SubtitleStyle } from "./subtitles";
import { hasCjk, scriptsInText, scriptFontUrl } from "@/lib/scriptFonts";

/**
 * Converts an already-written input file to a different container/codec,
 * extracted from ConvertVideo.tsx. MP4 uses libx264/aac (the reliable,
 * broadly-compatible path); WebM's VP9/Opus path is included for
 * completeness but not currently reachable from any tool's UI -- see
 * ConvertVideo.tsx's own comment for why (an unresolved ffmpeg.wasm
 * crash on VP9/Opus, not something fixable from this side).
 */
export async function convertVideoFormat(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  format: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal
): Promise<{ data: Uint8Array; rewrapped: boolean }> {
  // Re-wrap instead of re-encode: an H.264 + AAC (or silent) file going into MP4 / MOV only needs a new
  // container. That takes seconds, loses no quality and cannot grow the file (a re-encode made MOV -> MP4
  // about 49% bigger).
  if (format === "mp4" || format === "mp4-web" || format === "mov") {
    try {
      const probe = await probeMedia(ffmpeg, inputPath);
      if (probe.vcodec === "h264" && (!probe.hasAudio || probe.acodec === "aac")) {
        await execWithProgress(ffmpeg, ["-i", inputPath, "-c", "copy", ...(format === "mov" ? [] : ["-movflags", "+faststart"]), outputPath], onProgress, signal);
        return { data: (await ffmpeg.readFile(outputPath)) as Uint8Array, rewrapped: true };
      }
    } catch (err) {
      if (signal?.aborted) throw err;
      /* probing or re-wrapping failed: fall through to a normal conversion */
    }
  }

  let knownDuration: number | undefined;
  try { const d = (await probeMedia(ffmpeg, inputPath)).duration; if (d > 0) knownDuration = d; } catch { /* progress falls back to ffmpeg's own */ }
  // WebM: VP8 + Vorbis (much more reliable in ffmpeg.wasm than VP9 + Opus). Capped to 1280 wide.
  let args: string[];
  if (format === "webm") {
    args = ["-i", inputPath, "-vf", "scale='min(1280,iw)':-2", "-c:v", "libvpx", "-deadline", "realtime", "-cpu-used", "8", "-b:v", "1M", "-c:a", "libvorbis", "-b:a", "96k", "-f", "webm", outputPath];
  } else {
    args = ["-i", inputPath, "-c:v", "libx264", "-preset", "veryfast", "-crf", "23", "-pix_fmt", "yuv420p", "-c:a", "aac", "-b:a", "128k", ...(format === "mov" ? [] : ["-movflags", "+faststart"]), outputPath];
  }
  await execWithProgress(ffmpeg, args, onProgress, signal, knownDuration);
  return { data: (await ffmpeg.readFile(outputPath)) as Uint8Array, rewrapped: false };
}

export type TrimMode = "exact" | "fast";

export interface TrimResult {
  data: Uint8Array;
  /** Extension of the produced file ("mp4" for exact; the input's own for fast). */
  extension: string;
  /** Fast mode produced no usable picture, so the clip was cut again with re-encoding. */
  fellBack: boolean;
  /** Fast mode: roughly how many seconds before the requested start the clip actually begins (0 when exact). */
  startedEarlyBy?: number;
}

/**
 * Cuts one segment [start, end] (seconds) out of an already-written input file.
 *  - "exact": re-encodes, so the cut is at precisely the time asked and always has a picture.
 *  - "fast": copies the streams without re-encoding (quality identical, seconds), but can only start on
 *    a key frame. When the start is not on one, the old code produced a file with sound and no picture
 *    (or one that would not play). The result is now checked, and if the picture is missing or far too
 *    short the segment is cut again in exact mode - a picture-less file is never returned.
 */
export async function trimVideoSegment(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputBase: string,
  startSeconds: string,
  endSeconds: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  mode: TrimMode = "exact",
  inputExt = "mp4",
  /** Cap for the re-encoded picture (kbit/s) so a trimmed clip is not bigger than its source; 0 = no cap. */
  maxVideoKbps = 0,
): Promise<TrimResult> {
  const start = Number(startSeconds), end = Number(endSeconds);
  const duration = Math.max(0.1, end - start);
  const exactOut = `${outputBase}.mp4`;
  const runExact = async () => {
    await execWithProgress(
      ffmpeg,
      ["-ss", String(start), "-i", inputPath, "-t", duration.toFixed(3), "-c:v", "libx264", "-preset", "veryfast", "-crf", "23", ...(maxVideoKbps > 0 ? ["-maxrate", `${Math.round(maxVideoKbps)}k`, "-bufsize", `${Math.round(maxVideoKbps * 2)}k`] : []), "-pix_fmt", "yuv420p", "-c:a", "aac", "-b:a", "128k", "-movflags", "+faststart", exactOut],
      onProgress,
      signal,
      duration,
    );
    return (await ffmpeg.readFile(exactOut)) as Uint8Array;
  };

  if (mode === "exact") return { data: await runExact(), extension: "mp4", fellBack: false };

  const fastOut = `${outputBase}.${inputExt}`;
  await execWithProgress(
    ffmpeg,
    ["-ss", String(start), "-i", inputPath, "-t", duration.toFixed(3), "-c", "copy", "-avoid_negative_ts", "make_zero", fastOut],
    onProgress,
    signal,
  );
  try {
    const probe = await probeMedia(ffmpeg, fastOut);
    // A usable fast cut has a picture, is about as long as asked, and its first frame can be decoded.
    if (probe.hasVideo && probe.duration >= duration * 0.5 && probe.duration <= duration + 2.5) {
      await execWithProgress(ffmpeg, ["-i", fastOut, "-frames:v", "1", "-f", "image2", "check_frame.png"], () => {}, signal);
      const frame = (await ffmpeg.readFile("check_frame.png")) as Uint8Array;
      if (frame.length > 200) return { data: (await ffmpeg.readFile(fastOut)) as Uint8Array, extension: inputExt, fellBack: false, startedEarlyBy: Math.max(0, Math.round((probe.duration - duration) * 10) / 10) };
    }
  } catch { /* unreadable result: fall through to the exact cut */ }
  return { data: await runExact(), extension: "mp4", fellBack: true };
}

export interface MergeVideosResult {
  data: Uint8Array;
  /** How the clips were joined: untouched ("copy") or each brought to one size/frame rate first ("reencode"). */
  mode: "copy" | "reencode";
  /** True when none of the clips had sound. */
  noSound: boolean;
  /** Clips that had no sound while others did (silence was added for them). */
  silentClips: number;
  width: number;
  /** true when at least one clip was resized to fit the output size */
  fitted: boolean;
  height: number;
  /** Frame rate of the output; clips at another rate were converted to it. */
  fps: number;
  fpsMismatch: boolean;
  /** Seconds of crossfade at each join (0 = hard cuts). */
  crossfade: number;
}

/**
 * Merges multiple video files into one. Each clip is probed first; clips that differ in size, frame
 * rate or audio layout are letterboxed / converted to match (see mergePlan.ts), and a clip without
 * sound gets silence. Clips that already match are joined without re-encoding.
 */
export async function mergeVideos(
  ffmpeg: FFmpeg,
  files: File[],
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  onStatus?: (message: string) => void,
  shape: MergeShape = "first",
  fill = false,
  crossfade = false,
): Promise<MergeVideosResult> {
  const probes: MediaProbe[] = [];
  const inputArgs: string[] = [];
  const names: string[] = [];
  for (let i = 0; i < files.length; i++) {
    const ext = (files[i].name.match(/\.([a-z0-9]+)$/i)?.[1] ?? "mp4").toLowerCase();
    const name = `in${i}.${ext}`;
    onStatus?.(`Reading clip ${i + 1} of ${files.length}...`);
    await ffmpeg.writeFile(name, await fetchFile(files[i]));
    const probe = await probeMedia(ffmpeg, name);
    if (!probe.hasVideo) throw new Error(`"${files[i].name}" has no video in it, so it can't be merged.`);
    probes.push(probe);
    names.push(name);
    inputArgs.push("-i", name);
  }
  const plan = planMerge(probes, shape, fill, crossfade ? CROSSFADE_SECONDS : 0);
  const totalDuration = plan.totalSeconds || undefined;
  const silentClips = plan.withAudio ? probes.filter((p) => !p.hasAudio).length : 0;

  if (plan.mode === "copy") {
    onStatus?.("Joining clips without re-encoding...");
    await ffmpeg.writeFile("list.txt", names.map((n) => `file '${n}'`).join("\n"));
    await execWithProgress(ffmpeg, ["-f", "concat", "-safe", "0", "-i", "list.txt", "-c", "copy", "-movflags", "+faststart", "output.mp4"], onProgress, signal, totalDuration);
  } else {
    onStatus?.(`Merging clips (matching them to ${plan.width} x ${plan.height})...`);
    await execWithProgress(ffmpeg, [...inputArgs, ...mergeEncodeArgs(plan)], onProgress, signal, totalDuration);
  }
  const data = (await ffmpeg.readFile("output.mp4")) as Uint8Array;
  return { data, mode: plan.mode, noSound: !plan.withAudio, silentClips, width: plan.width, height: plan.height, fitted: plan.fitted, fps: plan.fps, fpsMismatch: plan.fpsMismatch, crossfade: plan.crossfade };
}

/**
 * Converts an already-written input file to an animated GIF, extracted
 * from VideoToGif.tsx. Two-pass approach (generate a color palette
 * first, then encode using it) rather than a direct one-step GIF
 * encode, which uses a poor default palette and looks noticeably
 * worse -- the standard, well-established technique for GIFs that
 * don't look muddy or banded.
 */
export interface GifOptions {
  startSeconds?: number;
  lengthSeconds?: number;
  width?: number;
  fps?: number;
  /** Playback speed: 0.5 = half speed, 2 = double. */
  speed?: number;
  /** Play once instead of looping forever. */
  playOnce?: boolean;
}

export async function videoToGif(
  ffmpeg: FFmpeg,
  inputPath: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  onStatus?: (message: string) => void,
  opts: GifOptions = {}
): Promise<Uint8Array> {
  const { startSeconds = 0, lengthSeconds, width = 480, fps = 12, speed = 1, playOnce = false } = opts;
  const range = [...(startSeconds > 0 ? ["-ss", String(startSeconds)] : []), ...(lengthSeconds ? ["-t", String(lengthSeconds)] : [])];
  const filter = `${speed !== 1 ? `setpts=PTS/${speed},` : ""}fps=${fps},scale='min(${width},iw)':-2:flags=lanczos`;
  onStatus?.("Generating color palette...");
  await execWithProgress(ffmpeg, [...range, "-i", inputPath, "-vf", `${filter},palettegen=stats_mode=diff`, "palette.png"], (f) => onProgress(f * 0.3), signal);

  onStatus?.("Encoding GIF...");
  await execWithProgress(
    ffmpeg,
    [...range, "-i", inputPath, "-i", "palette.png", "-lavfi", `${filter}[x];[x][1:v]paletteuse=dither=bayer:bayer_scale=4`, ...(playOnce ? ["-loop", "-1"] : []), "output.gif"],
    (f) => onProgress(0.3 + f * 0.7),
    signal
  );

  return ffmpeg.readFile("output.gif") as Promise<Uint8Array>;
}

/**
 * Burns subtitles into the picture (always a full re-encode). The SRT is checked first so a mistake is
 * reported by line number; it is turned into an ASS file sized to the video so the text scales properly and
 * every script (Hindi, Tamil, Arabic, Thai ...) uses its own bundled Noto font - libass has no system fonts
 * in the browser, which is why non-Latin text used to come out as empty boxes.
 */
export async function burnSubtitles(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  srtText: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
  style: SubtitleStyle = DEFAULT_SUBTITLE_STYLE
): Promise<Uint8Array> {
  const cues = parseSrt(srtText);
  const all = cues.map((c) => c.text).join("\n");
  if (hasCjk(all)) {
    throw new Error("Chinese, Japanese and Korean subtitles aren't supported yet: their fonts are too large to bundle. Hindi, Tamil, Telugu, Bengali, Arabic, Thai and other scripts are supported.");
  }
  const probe = await probeMedia(ffmpeg, inputPath);
  if (!probe.hasVideo) throw new Error("This file has no picture to put subtitles on.");
  if (needsBrowserShaping(cues)) {
    const { strips, blank, geo } = await renderCueStrips(cues, probe.width, probe.height, style);
    await ffmpeg.writeFile("blank.png", blank);
    for (let i = 0; i < strips.length; i++) await ffmpeg.writeFile(`strip${i}.png`, strips[i]);
    await ffmpeg.writeFile("strips.txt", buildStripList(cues));
    await execWithProgress(
      ffmpeg,
      ["-i", inputPath, "-f", "concat", "-safe", "0", "-i", "strips.txt", "-filter_complex", `[1:v]format=rgba[s];[0:v][s]overlay=0:${geo.y}:eof_action=pass:format=auto,format=yuv420p[v]`, "-map", "[v]", "-map", "0:a?", "-c:v", "libx264", "-preset", "veryfast", "-crf", "23", "-c:a", "aac", "-b:a", "128k", "-movflags", "+faststart", outputPath],
      onProgress,
      signal
    );
    return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
  }
  await ffmpeg.writeFile("subs.ass", buildAss(cues, probe.width, probe.height, style));
  await ffmpeg.writeFile("Roboto-Regular.ttf", await fetchFile("/fonts/Roboto-Regular.ttf"));
  for (const sc of scriptsInText(all)) {
    await ffmpeg.writeFile(sc.file, await fetchFile(scriptFontUrl(sc.file)));
  }
  await execWithProgress(
    ffmpeg,
    ["-i", inputPath, "-vf", "subtitles=subs.ass:fontsdir=.", "-c:v", "libx264", "-preset", "veryfast", "-crf", "23", "-pix_fmt", "yuv420p", "-c:a", "aac", "-b:a", "128k", "-movflags", "+faststart", outputPath],
    onProgress,
    signal
  );
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}

const srtTime = (t: number) => {
  const ms = Math.round(t * 1000), z = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${z(Math.floor(ms / 3600000))}:${z(Math.floor(ms / 60000) % 60)}:${z(Math.floor(ms / 1000) % 60)},${z(ms % 1000, 3)}`;
};

/** Whether the picture and sound can go into an MP4 untouched (so a subtitle track can be added without re-encoding). */
export const canCopyIntoMp4 = (p: { vcodec: string; acodec: string; hasAudio: boolean }) =>
  ["h264", "hevc", "mpeg4"].includes(p.vcodec) && (!p.hasAudio || ["aac", "mp3", "ac3"].includes(p.acodec));

/**
 * Adds the subtitles as a separate track that the viewer can switch on and off: seconds, no quality loss, no size growth.
 * Only for videos whose streams fit MP4 as they are (see canCopyIntoMp4).
 */
export async function addSoftSubtitles(
  ffmpeg: FFmpeg,
  inputPath: string,
  outputPath: string,
  srtText: string,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal
): Promise<Uint8Array> {
  const cues = parseSrt(srtText);
  const srt = cues.map((c, i) => `${i + 1}\n${srtTime(c.start)} --> ${srtTime(c.end)}\n${c.text}\n`).join("\n");
  await ffmpeg.writeFile("soft.srt", srt);
  await execWithProgress(
    ffmpeg,
    ["-i", inputPath, "-i", "soft.srt", "-map", "0:v", "-map", "0:a?", "-map", "1:0", "-c:v", "copy", "-c:a", "copy", "-c:s", "mov_text", "-metadata:s:s:0", "language=eng", "-movflags", "+faststart", outputPath],
    onProgress,
    signal
  );
  return ffmpeg.readFile(outputPath) as Promise<Uint8Array>;
}

/**
 * Captures the current frame of a playing/seeked HTMLVideoElement as a
 * PNG data URL, extracted from ThumbnailGen.tsx. Doesn't use ffmpeg at
 * all -- this was already a small, pure canvas operation, just inline
 * in the component; moved here for the same single-import-point
 * consistency as the ffmpeg-based operations above.
 */
export function captureVideoFrame(video: HTMLVideoElement): string {
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext("2d")!.drawImage(video, 0, 0);
  return canvas.toDataURL("image/png");
}

/** One frame as a PNG through ffmpeg: for videos the browser cannot play (for example iPhone HEVC in many browsers). */
export async function frameWithFfmpeg(ffmpeg: FFmpeg, inputPath: string, seconds: number): Promise<Uint8Array> {
  await ffmpeg.exec(["-ss", String(Math.max(0, seconds)), "-i", inputPath, "-frames:v", "1", "-y", "frame.png"]);
  return ffmpeg.readFile("frame.png") as Promise<Uint8Array>;
}
